import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { User, Key, FileCheck, Phone, Globe } from "lucide-react";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import CountryFlag from "@/components/country/CountryFlag";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Constituency } from "@/types/geography";

interface VerificationStep1Props {
  formData: any;
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  setStepValid: (isValid: boolean) => void;
}

const VerificationStep1 = ({
  formData,
  setFormData,
  setStepValid,
}: VerificationStep1Props) => {
  const [countries, setCountries] = useState<any[]>([]);
  const [constituencies, setConstituencies] = useState<Constituency[]>([]);
  const [loadingConstituencies, setLoadingConstituencies] = useState(false);

  useEffect(() => {
    const fetchCountries = async () => {
      const { data, error } = await supabase
        .from("countries")
        .select("code, name, active")
        .eq("active", true)
        .order("name");

      if (error) {
        console.error("Error fetching countries:", error);
        return;
      }

      setCountries(data || []);
    };

    fetchCountries();

    if (
      formData.personalInfo.residency_status === null ||
      formData.personalInfo.residency_status === undefined
    ) {
      setFormData((prev) => ({
        ...prev,
        personalInfo: {
          ...prev.personalInfo,
          residency_status: "",
        },
      }));
    }
  }, []);

  // Fetch constituencies when country changes
  useEffect(() => {
    const fetchConstituencies = async () => {
      if (!formData.verificationCountry) {
        setConstituencies([]);
        return;
      }

      setLoadingConstituencies(true);
      try {
        const { data, error } = await supabase
          .from("constituencies")
          .select("*")
          .eq("country_code", formData.verificationCountry)
          .eq("active", true)
          .order("name");

        if (error) throw error;
        setConstituencies(data || []);
      } catch (error) {
        console.error("Error fetching constituencies:", error);
        setConstituencies([]);
      } finally {
        setLoadingConstituencies(false);
      }
    };

    fetchConstituencies();
  }, [formData.verificationCountry]);

  // Reset constituency when country changes
  useEffect(() => {
    if (formData.verificationCountry) {
      setFormData((prev) => ({
        ...prev,
        verificationConstituency: "",
      }));
    }
  }, [formData.verificationCountry]);

  // Report validity to parent
  useEffect(() => {
    const status = formData.personalInfo.residency_status?.toLowerCase();
    const valid =
      !!formData.verificationCountry &&
      !!formData.verificationConstituency &&
      (status === "resident" || status === "expatriate");
    setStepValid(valid);
  }, [formData, setStepValid]);

  const handleCountryChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      verificationCountry: value,
      verificationConstituency: "", // Reset constituency when country changes
    }));
  };

  const handleConstituencyChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      verificationConstituency: value,
    }));
  };

  const handleResidencyStatusChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      personalInfo: {
        ...prev.personalInfo,
        residency_status: value,
      },
    }));
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold mb-2 text-gray-900 dark:text-gray-100">
          Welcome to the Identity Verification Process
        </h2>
        <p className="text-muted-foreground">
          This process helps us verify your identity, which is important for the
          integrity of our platform. Please select the country you want to be
          verified for, your constituency, and your residency status.
        </p>
      </div>

      <Card className="border-nation-purple/20">
        <CardHeader className="bg-nation-purple/5 border-b border-nation-purple/10">
          <CardTitle className="flex items-center gap-2 text-lg text-gray-900 dark:text-gray-100">
            <Globe className="h-5 w-5 text-nation-purple" />
            Verification Location
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Which country are you verifying for?
                </label>
                <Select
                  value={formData.verificationCountry}
                  onValueChange={handleCountryChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map((country) => (
                      <SelectItem key={country.code} value={country.code}>
                        <div className="flex items-center gap-2">
                          <CountryFlag
                            countryCode={country.code}
                            className="h-4 w-5"
                          />
                          <span>{country.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  This determines which country-specific verification rules will
                  apply and which polls you can access.
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Which constituency are you in?
                </label>
                <Select
                  value={formData.verificationConstituency || ""}
                  onValueChange={handleConstituencyChange}
                  disabled={!formData.verificationCountry || loadingConstituencies}
                >
                  <SelectTrigger>
                    <SelectValue 
                      placeholder={
                        !formData.verificationCountry 
                          ? "Select country first" 
                          : loadingConstituencies 
                          ? "Loading constituencies..." 
                          : constituencies.length === 0
                          ? "No constituencies available"
                          : "Select constituency"
                      } 
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {constituencies.map((constituency) => (
                      <SelectItem key={constituency.id} value={constituency.code}>
                        {constituency.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Your constituency determines which local polls and representatives you can access.
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  What is your residency status for this country?
                </label>
                <RadioGroup
                  value={formData.personalInfo.residency_status}
                  onValueChange={handleResidencyStatusChange}
                  className="grid grid-cols-1 md:grid-cols-2 gap-2"
                >
                  <div className="flex items-center space-x-2 border rounded-md p-3 hover:bg-slate-50 dark:hover:bg-gray-700">
                    <RadioGroupItem value="resident" id="resident" />
                    <label
                      htmlFor="resident"
                      className="text-sm font-medium cursor-pointer flex-grow text-gray-900 dark:text-gray-100"
                    >
                      Resident
                    </label>
                  </div>
                  <div className="flex items-center space-x-2 border rounded-md p-3 hover:bg-slate-50 dark:hover:bg-gray-700">
                    <RadioGroupItem value="expatriate" id="expatriate" />
                    <label
                      htmlFor="expatriate"
                      className="text-sm font-medium cursor-pointer flex-grow text-gray-900 dark:text-gray-100"
                    >
                      Expatriate
                    </label>
                  </div>
                </RadioGroup>
                <p className="text-xs text-muted-foreground mt-1">
                  Expatriates will be required to provide additional
                  documentation to verify their status.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Alert className="bg-nation-purple/10 border-nation-purple/20">
        <AlertTitle className="text-nation-purple">What to expect</AlertTitle>
        <AlertDescription className="text-muted-foreground space-y-3">
          <p>During this process, you'll be asked to provide:</p>
          <ul className="space-y-3">
            <li className="flex items-center gap-2">
              <User className="h-5 w-5 text-nation-purple" />
              <span>Your personal information</span>
            </li>
            <li className="flex items-center gap-2">
              <Key className="h-5 w-5 text-nation-purple" />
              <span>Your residency status (resident, expatriate, etc.)</span>
            </li>
            <li className="flex items-center gap-2">
              <FileCheck className="h-5 w-5 text-nation-purple" />
              <span>At least one government-issued ID document</span>
            </li>
            <li className="flex items-center gap-2">
              <Phone className="h-5 w-5 text-nation-purple" />
              <span>A phone number for verification</span>
            </li>
          </ul>
        </AlertDescription>
      </Alert>

      <div className="space-y-4">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100">Document Requirements</h3>
        <ul className="list-disc list-inside space-y-2 text-muted-foreground">
          <li>
            Government-issued photo ID (passport, driver's license, national ID
            card)
          </li>
          <li>Documents must be valid (not expired)</li>
          <li>Clear, legible scans or photos of the entire document</li>
          <li>Maximum file size: 5MB per document</li>
          <li>Accepted formats: PDF, JPEG, PNG</li>
        </ul>
      </div>

      <p className="italic text-sm text-gray-700 dark:text-gray-300">
        Click "Next" to begin the verification process.
      </p>
    </div>
  );
};

export default VerificationStep1;
